import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Save,
  Loader2,
  Check,
  Globe,
  GlobeLock,
  Link as LinkIcon,
} from 'lucide-react';
import { useToast } from '../../ui/Toast';

export type EditorTab = 'content' | 'workflow' | 'share' | 'responses';

const TAB_LABELS: Record<EditorTab, string> = {
  content: 'Conteúdo',
  workflow: 'Workflow',
  share: 'Compartilhar',
  responses: 'Resultados',
};

interface EditorTopBarProps {
  title: string;
  onTitleChange: (title: string) => void;
  formId?: string;
  slug?: string;
  username?: string;
  isPublished: boolean;
  hasChanges: boolean;
  onSave: () => void;
  onPublish: () => void;
  onBack: () => void;
  onAddField: () => void;
  activeTab: EditorTab;
  onTabChange: (tab: EditorTab) => void;
  user: { avatar_url?: string; full_name?: string } | null;
  isSaving: boolean;
}

const TABS: EditorTab[] = ['content', 'workflow', 'share', 'responses'];

export default function EditorTopBar({
  title,
  onTitleChange,
  formId,
  slug,
  username,
  isPublished,
  hasChanges,
  onSave,
  onPublish,
  onBack,
  onAddField,
  activeTab,
  onTabChange,
  user,
  isSaving,
}: EditorTopBarProps) {
  const { showToast } = useToast();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditingTitle) setLocalTitle(title);
  }, [title, isEditingTitle]);

  useEffect(() => {
    if (isEditingTitle) titleInputRef.current?.select();
  }, [isEditingTitle]);

  const commitTitle = () => {
    setIsEditingTitle(false);
    const trimmed = localTitle.trim();
    if (trimmed && trimmed !== title) onTitleChange(trimmed);
    else setLocalTitle(title);
  };

  const handleCopyLink = async () => {
    if (!formId) return;
    const origin = window.location.origin;
    const url = slug && username ? `${origin}/@${username}/${slug}` : `${origin}/f/${formId}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('success', 'Link copiado!');
    } catch {
      showToast('error', 'Falha ao copiar link');
    }
  };

  const initials = user?.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

  return (
    <div className="liquid-glass relative z-10 flex items-center px-4 py-2 border-b border-white/5">
      {/* Left: back + title + add */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <button
          onClick={onBack}
          className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
          title="Voltar"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>

        {/* Editable title — matches sidebar width minus back button; compressed below xl */}
        <div className="w-28 lg:w-36 xl:w-48 min-w-0 shrink-0">
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              value={localTitle}
              onChange={e => setLocalTitle(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={e => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') { setLocalTitle(title); setIsEditingTitle(false); } }}
              className="w-full bg-transparent text-white text-lg font-semibold outline-none border-b border-primary-500 px-1"
            />
          ) : (
            <button
              onClick={() => setIsEditingTitle(true)}
              className="w-full text-white text-lg font-semibold truncate hover:text-primary-300 transition-colors cursor-text px-1 text-left"
            >
              {title}
            </button>
          )}
        </div>

        {/* Add field — only visible on content tab */}
        {activeTab === 'content' && (
          <button
            onClick={onAddField}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-neutral-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors shrink-0"
            title="Adicionar campo"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden xl:inline">Adicionar</span>
          </button>
        )}

        {hasChanges && !isSaving && (
          <span className="text-amber-400 text-lg animate-pulse ml-1 shrink-0" title="Alterações não salvas">●</span>
        )}
      </div>

      {/* Center: tab navigation — scrollable below xl to avoid bar overflow (D2) */}
      <div className="flex items-center gap-2 xl:gap-3 text-sm min-w-0 overflow-x-auto shrink xl:min-w-fit xl:overflow-visible xl:shrink-0" role="tablist" aria-label="Editor sections">
        {TABS.map((tab, i) => {
          const disabled = !formId && (tab === 'share' || tab === 'responses');
          const isActive = activeTab === tab;

          return (
            <React.Fragment key={tab}>
              {i > 0 && <span className="hidden xl:inline text-neutral-600 select-none" aria-hidden="true">|</span>}
              <button
                role="tab"
                aria-selected={isActive}
                onClick={() => !disabled && onTabChange(tab)}
                disabled={disabled}
                className={`pb-1 border-b-2 transition-colors shrink-0 whitespace-nowrap ${
                  disabled
                    ? 'text-neutral-600 cursor-not-allowed border-transparent'
                    : isActive
                      ? 'text-white font-medium border-primary-500'
                      : 'text-neutral-400 hover:text-white border-transparent'
                }`}
              >
                {TAB_LABELS[tab]}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1 flex-1 justify-end">
        <button
          onClick={handleCopyLink}
          disabled={!formId}
          className={`p-2 rounded-lg transition-colors ${
            formId ? 'text-neutral-400 hover:text-white hover:bg-white/10' : 'text-neutral-600 opacity-40 cursor-not-allowed'
          }`}
          title="Copiar link"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </button>

        <button
          onClick={onSave}
          disabled={isSaving || !hasChanges}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            isSaving
              ? 'text-primary-400 bg-primary-500/10 cursor-wait'
              : hasChanges
                ? 'text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30'
                : 'text-neutral-600 cursor-default'
          }`}
          title={isSaving ? 'Salvando...' : hasChanges ? 'Salvar (Ctrl+S)' : 'Sem alterações'}
        >
          {isSaving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : hasChanges ? (
            <Save className="w-3.5 h-3.5" />
          ) : (
            <Check className="w-3.5 h-3.5" />
          )}
          {(isSaving || hasChanges) && (
            <span className="hidden xl:inline">{isSaving ? 'Salvando...' : 'Salvar'}</span>
          )}
        </button>

        <button
          onClick={onPublish}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white border border-primary-500 hover:bg-primary-500 transition-colors"
          title={isPublished ? 'Despublicar' : 'Publicar'}
        >
          {isPublished ? <GlobeLock className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />}
          <span className="hidden xl:inline">{isPublished ? 'Despublicar' : 'Publicar'}</span>
        </button>

        <Link to="/dashboard/profile" className="ml-2">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold border border-white/10">
              {initials}
            </div>
          )}
        </Link>
      </div>
    </div>
  );
}
